import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
    getLineValidateItemCounter,
    getPolygonValidateItemCounter,
    startLineAnalysis,
    startPolygonAnalysis
} from "api/layer";
import FathomIcon from "components/fathomIcon/fathomIcon";
import Svg from "components/svg/svg";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { DrawShapeSource } from "helpers/models";
import { MapContext } from "helpers/map";
import {
    buildLineAnalysisParams,
    buildLineValidateParams
} from "../shared/line-analysis-params";
import {
    buildNodeCoordinates,
    reportHazardLayerStatistics
} from "helpers/layerStatistics";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./fathom-popup.module.scss";

const FATHOM_LAYER_ARRAY = [
    "FL_Fluvial_Global_Fathom",
    "FL_Pluvial_Global_Fathom",
    "FL_Coastal_Global_Fathom"
];

export default function ShapeFathomPopup() {
    const [context, dispatch] = useContext(AppContext);
    const [t] = useTranslation();
    const { map } = useContext(MapContext);

    const [charging, setCharging] = useState(0);
    const [usedPrcentage, setUsedPrcentage] = useState(0); // 0: normal, 1: warn, 2: overflow
    const [donotAskAgain, setDonotAskAgain] = useState(false);

    const selectedPolyline = useMemo(
        () => context.shapes.find((pl) => pl.id === context.selectedShapeId),
        [context.shapes, context.selectedShapeId]
    );

    const isPolygonShape =
        selectedPolyline?.source === DrawShapeSource.POLYGON ||
        selectedPolyline?.source === DrawShapeSource.SHAPE_POLYGON;
    const selectedButtons = (isPolygonShape ? context.polygonSelectLayer : context.lineSelectLayer) as string[][] | undefined;
    const selectedLayers = selectedButtons?.[0] || [];

    const onClose = () => {
        dispatch({ type: AppAction.setShapeFathomPopup, payload: undefined });
    };

    const runLineAnalysis = (layers: string[]) => {
        const params = buildLineAnalysisParams(
            selectedPolyline,
            map,
            layers,
            !!selectedPolyline?.hasShapeAnalysed
        );
        if (!params || !selectedPolyline) {
            return;
        }

        if (!selectedPolyline.aMapRef.zoom) {
            selectedPolyline.aMapRef.zoom = Math.floor(map.getZoom());
            dispatch({
                type: AppAction.setShapes,
                payload: context.shapes.map((pl) =>
                    pl.id === selectedPolyline.id ? { ...pl } : pl
                )
            });
        }

        dispatch({ type: AppAction.setCurrentShape, payload: selectedPolyline });
        
        // 根据形状类型分别设置分析状态和图例
        const isPolygon =
            selectedPolyline?.source === DrawShapeSource.POLYGON ||
            selectedPolyline?.source === DrawShapeSource.SHAPE_POLYGON;
        
        if (isPolygon) {
          dispatch({ type: AppAction.setPolygonAnalysisShapeId, payload: selectedPolyline.id });
          dispatch({ type: AppAction.setPolygonExpandedLayerId, payload: undefined });
          dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "running" });
          dispatch({ type: AppAction.setPolygonJobId, payload: undefined });
          dispatch({ type: AppAction.setPolygonAnalysisLegends, payload: context.hazardLegends?.filter((item) => layers.includes(item.layerId)) });
        } else {
          dispatch({ type: AppAction.setLineAnalysisShapeId, payload: selectedPolyline.id });
          dispatch({ type: AppAction.setLineAnalysisStatus, payload: "running" });
          dispatch({ type: AppAction.setLineJobId, payload: undefined });
          dispatch({ type: AppAction.setLineAnalysisLegends, payload: context.hazardLegends?.filter((item) => layers.includes(item.layerId)) });
        }

        const startTime = Date.now();
        const startAnalysis = isPolygon ? startPolygonAnalysis : startLineAnalysis;
        startAnalysis(params)
            .then((res: any) => {
                const responseTime = Date.now() - startTime;
                const nodeCoordinates = buildNodeCoordinates(params.nodes);

                if (!res.code || res.code === 400) {
                    reportHazardLayerStatistics({
                        eventType: selectedPolyline.source,
                        status: "0",
                        layers,
                        locationAmount: 1,
                        executeInterval: responseTime,
                        coordinates: nodeCoordinates,
                        traceInfo: res.message
                    });

                    dispatch({
                        type: AppAction.addSnackBarParams,
                        payload: {
                            title: "tip",
                            titleIconColor: "red",
                            titleIcon: "error",
                            resMessage: context.language === "en" ? res.data.messageEN : res.data.messageCN
                        }
                    });
                    if (isPolygon) {
                      dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
                    } else {
                      dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
                    }
                    return;
                }

                if (res?.code === 200 && res?.data?.jobId) {
                    reportHazardLayerStatistics({
                        eventType: selectedPolyline.source,
                        status: "1",
                        layers,
                        locationAmount: 1,
                        executeInterval: responseTime,
                        coordinates: nodeCoordinates
                    });

                    const containsFathomLayer = layers.some((layerId) =>
                        FATHOM_LAYER_ARRAY.includes(layerId)
                    );

                    if (containsFathomLayer) {
                        const updatedShapes = context.shapes.map((pl) =>
                            pl.id === selectedPolyline.id
                                ? { ...pl, hasShapeAnalysed: true }
                                : pl
                        );
                        dispatch({ type: AppAction.setShapes, payload: updatedShapes });
                        const updatedCurrentShape = updatedShapes.find(
                            (pl) => pl.id === selectedPolyline.id
                        );
                        if (updatedCurrentShape) {
                            dispatch({ type: AppAction.setCurrentShape, payload: updatedCurrentShape });
                        }
                        if (selectedPolyline.shapeId) {
                            dispatch({
                                type: AppAction.updateMyShape,
                                payload: {
                                    id: selectedPolyline.shapeId,
                                    name: updatedCurrentShape?.name || selectedPolyline.name,
                                    type: updatedCurrentShape?.type || selectedPolyline.type,
                                    hasShapeAnalysed: true
                                }
                            });
                        }
                    }

                    if (isPolygon) {
                      dispatch({ type: AppAction.setPolygonJobId, payload: res.data.jobId });
                    } else {
                      dispatch({ type: AppAction.setLineJobId, payload: res.data.jobId });
                    }
                } else {
                    reportHazardLayerStatistics({
                        eventType: selectedPolyline.source,
                        status: "0",
                        layers,
                        locationAmount: 1,
                        executeInterval: responseTime,
                        coordinates: nodeCoordinates,
                        traceInfo: res.message
                    });
                    if (isPolygon) {
                      dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
                    } else {
                      dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
                    }
                }
            })
            .catch((error) => {
                const responseTime = Date.now() - startTime;
                reportHazardLayerStatistics({
                    eventType: selectedPolyline.source,
                    status: "0",
                    layers,
                    locationAmount: 1,
                    executeInterval: responseTime,
                    coordinates: buildNodeCoordinates(params.nodes),
                    traceInfo: error.message
                });
                if (isPolygon) {
                  dispatch({ type: AppAction.setPolygonAnalysisStatus, payload: "error" });
                } else {
                  dispatch({ type: AppAction.setLineAnalysisStatus, payload: "error" });
                }
            });

        dispatch({
            type: AppAction.setRightPanel,
            payload: isPolygon ? RightPanel.Polygon : RightPanel.Line
        });
        dispatch({ type: AppAction.setAnalyseShapeModal, payload: undefined });
        onClose();
    };

    const handleNo = () => {
        dispatch({ type: AppAction.setShapeAskAgain, payload: donotAskAgain });

        const nonFathomLayers = selectedLayers.filter(
            (layerId) => !FATHOM_LAYER_ARRAY.includes(layerId)
        );

        if (nonFathomLayers.length) {
            dispatch({ type: isPolygonShape ? AppAction.setPolygonSelectLayer : AppAction.setLineSelectLayer, payload: [nonFathomLayers] });
            runLineAnalysis(nonFathomLayers);
            return;
        }

        dispatch({ type: AppAction.setAnalyseShapeModal, payload: true });
        onClose();
    };

    useEffect(() => {
        if (context.shapeFathomPopup !== "line") {
            return;
        }

        if (!selectedLayers.length) {
            return;
        }

        const params = buildLineValidateParams(
            selectedPolyline,
            selectedLayers,
            !!selectedPolyline?.hasShapeAnalysed
        );
        if (!params) {
            return;
        }

        const validateItemCounter = isPolygonShape
            ? getPolygonValidateItemCounter
            : getLineValidateItemCounter;

        validateItemCounter(params).then((res: any) => {
            if (res.data.itemFathomQuotas && res.data.itemFathomQuotas > 0) {
                if (
                    (res.data.trItemFathomCount + (res.data.notExistFathomCount ?? 0)) >
                    res.data.itemFathomQuotas
                ) {
                    setUsedPrcentage(2);
                } else if (
                    res.data.trItemFathomCount / res.data.itemFathomQuotas >= 0.8
                ) {
                    setUsedPrcentage(1);
                } else {
                    setUsedPrcentage(0);
                }
            } else {
                setUsedPrcentage(0);
            }
            setCharging(res.data.notExistFathomCount ?? 0);
        });
    }, [context.shapeFathomPopup, selectedLayers, selectedPolyline]);

    return (
        <Dialog maxWidth="xs" open={context.shapeFathomPopup === "line"} onClose={onClose}>
            <DialogTitle className={styles.title}>{t("fathom.popup.title")}</DialogTitle>

            <DialogContent>
                <div className={styles.content}>
                    <div>{t("fathom.popup.text")}</div>
                    <div>{t("fathom.popup.charging", { num: charging })}</div>
                    {usedPrcentage === 1 && (
                        <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.warning") }}></span>
                    )}
                    {usedPrcentage === 2 && (
                        <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.error") }}></span>
                    )}
                </div>

                {selectedLayers
                    .filter((layerId) => FATHOM_LAYER_ARRAY.includes(layerId))
                    .map((layerId) => (
                        <div key={layerId} className={styles.item}>
                            <div>{Svg({ icon: layerId })()}</div>
                            <div style={{ marginLeft: "6px" }}>{t("layerId." + layerId)}</div>
                            <FathomIcon className={styles.premium} />
                        </div>
                    ))}

                <div>
                    <Checkbox
                        color="success"
                        onChange={() => setDonotAskAgain(!donotAskAgain)}
                        checked={donotAskAgain}
                    />
                    {t("fathom.popup.ask")}
                </div>
            </DialogContent>

            <DialogActions>
                <Button
                    color="success"
                    variant="outlined"
                    onClick={handleNo}
                    style={{ margin: "0 10px 10px 0" }}
                >
                    {t("buttons.no")}
                </Button>
                <Button
                    color="success"
                    variant="contained"
                    onClick={() => {
                        dispatch({ type: AppAction.setShapeAskAgain, payload: donotAskAgain });
                        runLineAnalysis(selectedLayers);
                    }}
                    style={{ margin: "0 10px 10px 0" }}
                    disabled={usedPrcentage === 2}
                >
                    {t("buttons.yes")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
